import React, { useState, useEffect } from 'react';
import QRCard from '../components/QRCard.jsx';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888' };

const STOREFRONT_URL = import.meta.env.VITE_STOREFRONT_URL || 'https://qrqy.shop';
// TODO(M3): Set VITE_API_BASE_URL to switch from mock data to real API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Fallback mock data — used when VITE_API_BASE_URL is not configured.
// TODO(M3): Remove once GET /api/qr-codes is live.
const MOCK_QR_CODES = [
  {
    code: 'XQ7R4P',
    destination: 'https://open.spotify.com/album/12345',
    scanCount: 31,
    lastScanned: '2026-03-24T18:32:00Z',
    productName: 'QR TEE',
    isActive: true,
  },
  {
    code: 'KN2M8A',
    destination: 'https://www.instagram.com/p/abcdef/',
    scanCount: 16,
    lastScanned: '2026-03-20T11:05:00Z',
    productName: 'QR HOODIE',
    isActive: true,
  },
];

function SkeletonCard() {
  return (
    <div style={{
      backgroundColor: '#111',
      padding: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    }}>
      {[140, 80, 60, 100].map((w, i) => (
        <div
          key={i}
          style={{
            height: i === 0 ? '80px' : '12px',
            width: `${w}px`,
            backgroundColor: 'rgba(255,255,255,0.06)',
            borderRadius: '2px',
          }}
        />
      ))}
    </div>
  );
}

export default function MyQRs() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchCodes() {
      if (!API_BASE_URL) {
        // No API configured — use mock data with simulated latency
        await new Promise((r) => setTimeout(r, 350));
        if (!cancelled) {
          setCodes(MOCK_QR_CODES);
          setLoading(false);
        }
        return;
      }

      // TODO(M3): Wire to real session — credentials: 'include' sends the HttpOnly auth cookie
      try {
        const res = await fetch(`${API_BASE_URL}/api/qr-codes`, {
          credentials: 'include',
        });

        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Session expired. Please sign in again.');
          }
          throw new Error(`Failed to load your pieces (${res.status}).`);
        }

        const data = await res.json();
        if (!cancelled) {
          // API returns { codes: [...] } shaped like ownership.getOwnedQRCodes results
          const mapped = (data.codes || []).map((q) => ({
            code: q.code,
            destination: q.destination_url,
            scanCount: q.scan_count ?? 0,
            lastScanned: q.last_scanned ?? null,
            productName: q.product_name ?? 'QRQY PIECE',
            isActive: Boolean(q.is_active),
          }));
          setCodes(mapped);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load your QR codes. Please refresh.');
          setLoading(false);
        }
      }
    }

    fetchCodes();
    return () => { cancelled = true; };
  }, []);

  const isEmpty = !loading && !error && codes.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
      {/* Header */}
      <div>
        <p style={{
          fontSize: '0.65rem',
          letterSpacing: '0.25em',
          color: COLORS.gold,
          margin: '0 0 0.5rem',
          fontWeight: 600,
        }}>
          INVENTORY
        </p>
        <h1 style={{
          fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
          fontWeight: 900,
          letterSpacing: '-0.01em',
          color: COLORS.white,
          margin: 0,
        }}>
          YOUR PIECES
        </h1>
        <p style={{
          marginTop: '0.5rem',
          fontSize: '0.82rem',
          color: COLORS.gray,
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
        }}>
          Each piece is a living QR code. Tap EDIT to control where it goes.
        </p>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1px',
          backgroundColor: 'rgba(255,255,255,0.06)',
        }}>
          {[0, 1].map((i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={{
          padding: '2rem',
          border: '1px solid rgba(224,92,92,0.25)',
          backgroundColor: 'rgba(224,92,92,0.06)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          alignItems: 'flex-start',
        }}>
          <p style={{ fontSize: '0.82rem', color: '#e05c5c', margin: 0 }}>{error}</p>
          <button
            onClick={() => { setError(null); setLoading(true); }}
            style={{
              padding: '0.6rem 1.5rem',
              backgroundColor: 'transparent',
              border: '1px solid rgba(224,92,92,0.4)',
              color: '#e05c5c',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.15em',
              cursor: 'pointer',
            }}
          >
            RETRY
          </button>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div style={{
          padding: '5rem 2rem',
          textAlign: 'center',
          border: `1px solid rgba(255,255,255,0.06)`,
        }}>
          <p style={{
            fontSize: '0.9rem',
            color: COLORS.gray,
            margin: '0 0 0.5rem',
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
          }}>
            No pieces registered yet.
          </p>
          <p style={{
            fontSize: '0.78rem',
            color: COLORS.gray,
            margin: '0 0 0.75rem',
            opacity: 0.6,
          }}>
            Purchase a QRQY piece and claim your code to see it here.
          </p>
          <p style={{
            fontSize: '0.72rem',
            color: COLORS.gold,
            margin: '0 0 2rem',
            opacity: 0.8,
          }}>
            After purchase → check your email for a claim link → come back here.
          </p>
          <a
            href={`${STOREFRONT_URL}/shop`}
            style={{
              display: 'inline-block',
              padding: '0.75rem 2rem',
              border: `1px solid ${COLORS.white}`,
              color: COLORS.white,
              textDecoration: 'none',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.18em',
            }}
          >
            SHOP THE DROP
          </a>
        </div>
      )}

      {/* QR code grid */}
      {!loading && !error && codes.length > 0 && (
        <>
          <p style={{
            fontSize: '0.7rem',
            color: COLORS.gray,
            letterSpacing: '0.1em',
            margin: 0,
          }}>
            {codes.length} {codes.length === 1 ? 'PIECE' : 'PIECES'} OWNED
          </p>

          {/* TODO(M3): Add sort/filter controls */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1px',
            backgroundColor: 'rgba(255,255,255,0.06)',
          }}>
            {codes.map((qr) => (
              <QRCard key={qr.code} {...qr} />
            ))}
          </div>

          {!API_BASE_URL && (
            <p style={{
              fontSize: '0.72rem',
              color: COLORS.gray,
              opacity: 0.5,
              textAlign: 'center',
            }}>
              Showing mock data — set VITE_API_BASE_URL for live data.
            </p>
          )}
        </>
      )}
    </div>
  );
}
